// client/src/pages/doctors/DoctorMessages.jsx - COMPLETE UPDATED VERSION
import React, { useState, useEffect, useRef } from 'react';
import { useTenant } from '../../../context/TenantContext';
import FeatureWrapper from '../../../components/common/FeatureWrapper';
import chatService from '../../../services/chatService';
import doctorService from '../../../services/doctorService';
import '../../../styles/components/doctor/DoctorMessages.css';

import neurolexLogo from '../../../assets/images/Neurolex_Logo_New.png';

// 🎥 VIDEO SESSION COMPONENTS
import VideoSessionInvitation from './VideoSessionInvitation';
import VideoSessionOverlay from './VideoSessionOverlay';
import CreateSessionModal from './CreateSessionModal';

const DoctorMessages = () => {
  const { currentTenant, getThemeStyles, platformName } = useTenant();
  const theme = getThemeStyles();
  
  // Existing state management
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [streamClient, setStreamClient] = useState(null);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // 🎥 VIDEO SESSION STATE
  const [showCreateSessionModal, setShowCreateSessionModal] = useState(false);
  const [activeVideoSession, setActiveVideoSession] = useState(null);
  const [sessionInvitations, setSessionInvitations] = useState(new Map());
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  // 🎥 ENHANCED: Better quick session request detection
  const detectQuickSessionRequest = (message) => {
    if (!message.text || message.user?.id === doctorInfo._id) {
      return false;
    }
    
    // Multiple detection patterns for robustness
    const quickSessionPatterns = [
      /quick\s+session\s+requested/i,
      /🎥.*quick.*session/i,
      /video.*session.*requested/i,
      /urgent.*session/i,
      /immediate.*session/i
    ];
    
    return quickSessionPatterns.some(pattern => pattern.test(message.text));
  };

  // 🎥 ENHANCED: Quick session request component
  const QuickSessionRequestMessage = ({ message, onAccept, onDecline }) => {
    const [responding, setResponding] = useState(false);
    
    const handleAccept = async () => {
      if (responding) return;
      setResponding(true);
      try {
        await onAccept(message);
      } finally {
        setResponding(false);
      }
    };
    
    const handleDecline = async () => {
      if (responding) return;
      setResponding(true);
      try {
        await onDecline(message);
      } finally {
        setResponding(false);
      }
    };
    
    return (
      <div className="quick-session-request-message">
        <div className="request-container">
          {/* Request Header */}
          <div className="request-header">
            <div className="request-icon-wrapper">
              <span className="request-icon">🎥</span>
            </div>
            <div className="request-title-section">
              <h4 className="request-title">Video Session Request</h4>
              <p className="request-subtitle">Patient is requesting an immediate session</p>
            </div>
            <div className="request-status">
              <span className="status-badge urgent">Urgent</span>
            </div>
          </div>
          
          {/* Request Content */}
          <div className="request-content">
            <div className="request-message-text">
              <span className="message-icon">💬</span>
              <span className="message-text">{message.text}</span>
            </div>
            
            <div className="request-details">
              <div className="detail-item">
                <span className="detail-icon">👤</span>
                <span className="detail-text">
                  From: {selectedPatient?.firstName} {selectedPatient?.lastName}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-icon">⏰</span>
                <span className="detail-text">
                  Requested: {formatMessageTime(message.created_at)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="request-actions">
            <button 
              className="request-btn accept-btn"
              onClick={handleAccept}
              disabled={responding}
            >
              {responding ? (
                <>
                  <div className="loading-spinner small"></div>
                  <span>Accepting...</span>
                </>
              ) : (
                <>
                  <span className="btn-icon">✅</span>
                  <span className="btn-text">Accept Session</span>
                </>
              )}
            </button>
            
            <button 
              className="request-btn decline-btn"
              onClick={handleDecline}
              disabled={responding}
            >
              {responding ? (
                <>
                  <div className="loading-spinner small"></div>
                  <span>Declining...</span>
                </>
              ) : (
                <>
                  <span className="btn-icon">❌</span>
                  <span className="btn-text">Decline</span>
                </>
              )}
            </button>
          </div>
          
          {/* Request Footer */}
          <div className="request-footer">
            <div className="footer-info">
              <span className="info-icon">ℹ️</span>
              <span className="info-text">
                Accepting will create an immediate 30-minute video session
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Initialize doctor info and Stream Chat
  useEffect(() => {
    const initializeMessaging = async () => {
      try {
        console.log('🚀 [DEBUG] Starting initializeMessaging...');
        setLoading(true);
        setError(null);
        
        // Get doctor profile
        console.log('🚀 [DEBUG] Getting doctor profile...');

        // Get user data from storage first
        const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          setCurrentUser(user);
        }

        const profileResponse = await doctorService.getProfile();
        console.log('🚀 [DEBUG] Profile response:', profileResponse);

        const profile = profileResponse.data || profileResponse;
        console.log('🚀 [DEBUG] Extracted profile:', profile);

        // Include profile picture
        setDoctorInfo({
          ...profile,
          profilePicture: profile.profilePicture
        });

        setCurrentUser(prev => ({
          ...prev,
          ...profile,
          profilePicture: profile.profilePicture
        }));
        
        console.log('🔍 Initializing messaging for doctor:', profile.firstName, profile.lastName);
        
        // Initialize Stream Chat
        console.log('🚀 [DEBUG] About to generate chat token...');
        const chatToken = await chatService.generateChatToken();
        console.log('🚀 [DEBUG] Chat token generated:', chatToken ? 'SUCCESS' : 'FAILED');
        
        console.log('🚀 [DEBUG] About to initialize Stream Chat...');
        const client = await chatService.initializeStreamChat(
          profile._id,
          {
            name: `Dr. ${profile.firstName} ${profile.lastName}`,
            userType: 'doctor',
            specialty: profile.specialty || profile.specialization || 'General',
            title: profile.title || 'Dr.',
            profilePicture: profile.profilePicture
          },
          chatToken
        );
        console.log('🚀 [DEBUG] Stream Chat client result:', client ? 'SUCCESS' : 'FAILED');
          
        setStreamClient(client);
        console.log('✅ Stream Chat initialized for doctor');
        
        // Pass the profile directly to loadPatients instead of using state
        await loadPatientsWithProfile(profile);
        
        // 🎥 Load upcoming sessions after initialization
        await loadUpcomingSessions();
        
      } catch (error) {
        console.error('❌ Error initializing messaging:', error);
        setError('Failed to initialize messaging system. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    initializeMessaging();
    
    // Cleanup on unmount
    return () => {
      if (streamClient) {
        streamClient.disconnectUser();
      }
    };
  }, []);

  // 🎥 Load upcoming sessions
  const loadUpcomingSessions = async () => {
    try {
      console.log('🎥 Loading upcoming sessions...');
      const response = await fetch('/api/chat/sessions/upcoming', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setUpcomingSessions(result.data || []);
        console.log(`✅ Loaded ${result.data?.length || 0} upcoming sessions`);
      }
    } catch (error) {
      console.error('❌ Error loading upcoming sessions:', error);
    }
  };

  const loadPatientsWithProfile = async (profile) => {
    try {
      console.log('🔍 Loading patients for messaging...');
      
      // Get appointments to find associated patients
      const appointmentsResponse = await doctorService.getAppointments();
      let appointments = [];
      
      if (appointmentsResponse.success && appointmentsResponse.data) {
        appointments = appointmentsResponse.data;
      } else if (Array.isArray(appointmentsResponse)) {
        appointments = appointmentsResponse;
      }
      
      console.log('📋 Found appointments:', appointments.length);
      
      // Extract unique patients from appointments
      const uniquePatients = [];
      const patientIds = new Set();
      
      appointments.forEach(appointment => {
        if (appointment.patient && 
            appointment.patient._id !== profile._id && 
            !patientIds.has(appointment.patient._id)) {
          
          patientIds.add(appointment.patient._id);
          uniquePatients.push({
            _id: appointment.patient._id,
            firstName: appointment.patient.firstName,
            lastName: appointment.patient.lastName,
            profilePicture: appointment.patient.profilePicture,
            email: appointment.patient.email,
            lastAppointment: appointment.appointmentDate,
            appointmentCount: appointments.filter(a => 
              a.patient?._id === appointment.patient._id && 
              a.patient._id !== profile._id
            ).length,
            // 🎥 Add next appointment info for session creation
            nextAppointment: appointments
              .filter(a => 
                a.patient?._id === appointment.patient._id && 
                new Date(a.appointmentDate) > new Date() &&
                a.status === 'Scheduled'
              )
              .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate))[0]
          });
        }
      });
      
      console.log('👥 Unique patients found (excluding doctor):', uniquePatients.length);
      console.log('🔍 Doctor ID to exclude:', profile._id);
      setPatients(uniquePatients);
      
    } catch (error) {
      console.error('❌ Error loading patients:', error);
      setError('Failed to load patients. Please try again.');
    }
  };

  // Filter patients based on search
  const filteredPatients = patients.filter(patient => {
    if (!searchTerm) return true;
    const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  // Enhanced selectPatient to handle video sessions
  const selectPatient = async (patient) => {
    try {
      if (!streamClient || !doctorInfo) {
        console.error('❌ Stream client or doctor info not available');
        return;
      }
      
      console.log('💬 Selecting patient for chat:', patient.firstName, patient.lastName);
      setSelectedPatient(patient);
      setMessages([]);
      setError(null);
      
      // Use the SAME channel ID format as Flutter
      const channelId = `doctor_${patient._id}_${doctorInfo._id}`;
      
      console.log('🔍 Using channel ID:', channelId);
      
      const channel = streamClient.channel('messaging', channelId, {
        name: `Dr. ${doctorInfo.firstName} ${doctorInfo.lastName} & ${patient.firstName} ${patient.lastName}`,
        members: [doctorInfo._id, patient._id],
        created_by_id: doctorInfo._id,
        doctor_id: doctorInfo._id,
        patient_id: patient._id,
        channel_type: 'doctor_patient'
      });
      
      // Watch the channel
      await channel.watch();
      setCurrentChannel(channel);
      
      // Load existing messages
      const state = await channel.query({
        messages: { limit: 50 }
      });
      
      const allMessages = state.messages || [];
      setMessages(allMessages);
      
      // 🎥 Extract session invitations from messages using new extraData format
      const sessionInvites = new Map();
      allMessages.forEach(message => {
        if (message.extraData?.message_type === 'video_session_invitation') {
          const data = message.extraData;
          sessionInvites.set(data.session_id, {
            sessionId: data.session_id,
            sessionTime: data.session_time,
            sessionDuration: data.session_duration,
            status: data.status,
            messageId: message.id
          });
        }
      });
      setSessionInvitations(sessionInvites);
      
      console.log('💬 Loaded', allMessages.length, 'messages');
      console.log('🎥 Found', sessionInvites.size, 'session invitations');
      
      // Listen for new messages
      channel.on('message.new', (event) => {
        console.log('📩 New message received:', event.message.text || event.message.type);
        setMessages(prev => [...prev, event.message]);
        
        // 🎥 Handle video session messages with new extraData format
        if (event.message.extraData?.message_type === 'video_session_invitation') {
          const data = event.message.extraData;
          setSessionInvitations(prev => new Map(prev.set(data.session_id, {
            sessionId: data.session_id,
            sessionTime: data.session_time,
            sessionDuration: data.session_duration,
            status: data.status,
            messageId: event.message.id
          })));
        }
      });
      
      // Scroll to bottom
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      
    } catch (error) {
      console.error('❌ Error selecting patient:', error);
      setError('Failed to load chat. Please try again.');
    }
  };

  // 🎥 Create video session
  const createVideoSession = async (sessionData) => {
    try {
      if (!currentChannel || !selectedPatient) {
        throw new Error('No active chat selected');
      }

      console.log('🎥 Creating video session...', sessionData);

      const response = await fetch('/api/chat/session/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          channelId: currentChannel.id,
          patientId: selectedPatient._id,
          appointmentId: sessionData.appointmentId,
          sessionTime: sessionData.sessionTime,
          sessionDuration: sessionData.sessionDuration
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create video session');
      }

      const result = await response.json();
      console.log('✅ Video session created:', result.data);

      // Close modal and reload upcoming sessions
      setShowCreateSessionModal(false);
      await loadUpcomingSessions();

      return result.data;
    } catch (error) {
      console.error('❌ Error creating video session:', error);
      setError(error.message);
      throw error;
    }
  };

  // 🎥 Join video session
  const joinVideoSession = async (sessionId) => {
    try {
      console.log('🎥 Attempting to join session:', sessionId);

      // Check if user can join the session
      const response = await fetch('/api/chat/session/join', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ sessionId })
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.message);
        return;
      }

      // Update session status to starting
      await updateSessionStatus(sessionId, 'starting');

      // Set active video session (this will open the video overlay)
      setActiveVideoSession({
        sessionId,
        userRole: 'doctor',
        patientInfo: selectedPatient,
        doctorInfo: doctorInfo
      });

      console.log('✅ Joined video session successfully');
    } catch (error) {
      console.error('❌ Error joining video session:', error);
      setError('Failed to join video session. Please try again.');
    }
  };

  // 🎥 Update session status
  const updateSessionStatus = async (sessionId, status) => {
    try {
      const response = await fetch('/api/chat/session/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sessionId,
          status,
          channelId: currentChannel?.id
        })
      });

      if (response.ok) {
        console.log('✅ Session status updated:', status);
      }
    } catch (error) {
      console.error('❌ Error updating session status:', error);
    }
  };

  // 🎥 End video session
  const endVideoSession = async () => {
    if (activeVideoSession) {
      await updateSessionStatus(activeVideoSession.sessionId, 'ended');
      setActiveVideoSession(null);
      console.log('✅ Video session ended');
    }
  };

  // 🎥 ENHANCED: Quick session handlers
 // ✅ FIXED: React (Doctor) Side - DoctorMessages.jsx
// In handleQuickSessionRequest function:

const handleQuickSessionRequest = async (message) => {
  try {
    console.log('🎥 ===== WEB: PROCESSING QUICK SESSION =====');
    console.log('🎥 Channel ID:', currentChannel.id);
    console.log('🎥 Patient:', selectedPatient._id);
    console.log('🎥 Doctor:', doctorInfo._id);
    
    // ✅ CRITICAL: Let backend create the deterministic session ID
    const sessionData = {
      sessionTime: new Date().toISOString(),
      sessionDuration: 30,
      appointmentId: null,
      sessionType: 'quick_session'  // ✅ Backend will create deterministic ID
    };
    
    console.log('🎥 Creating session via backend...');
    
    const response = await fetch('/api/chat/session/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        channelId: currentChannel.id,
        patientId: selectedPatient._id,
        appointmentId: sessionData.appointmentId,
        sessionTime: sessionData.sessionTime,
        sessionDuration: sessionData.sessionDuration,
        sessionType: sessionData.sessionType
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create video session');
    }
    
    const createdSession = await response.json();
    console.log('🎥 ===== BACKEND RESPONSE =====');
    console.log(createdSession);
    
    // ✅ Get the session ID that backend created
    const backendSessionId = createdSession.data?.sessionId || createdSession.data?.session_id;
    
    if (!backendSessionId) {
      throw new Error('Backend did not return session ID');
    }
    
    console.log('🎥 ✅ BACKEND CREATED SESSION ID:', backendSessionId);
    console.log('🎥 ✅ MOBILE SHOULD DETECT THIS SAME ID FROM MESSAGES');
    
    // ✅ Doctor joins with backend's session ID
    setActiveVideoSession({
      sessionId: backendSessionId,  // ✅ Use backend's ID
      userRole: 'doctor',
      patientInfo: selectedPatient,
      doctorInfo: doctorInfo
    });
    
    console.log('🎥 ✅ DOCTOR JOINING WITH SESSION ID:', backendSessionId);
    
  } catch (error) {
    console.error('❌ Error in quick session:', error);
    setError('Failed to create quick session: ' + error.message);
  }
};

  const handleDeclineQuickSession = async (message) => {
    try {
      console.log('🎥 Declining quick session request...');
      
      // Send decline message
      await currentChannel.sendMessage({
        text: '❌ I\'m not available for a video session right now. Please schedule an appointment through the app for our next session.',
        user_id: doctorInfo._id,
        sender_type: 'doctor',
        extraData: {
          message_type: 'video_session_response',
          response_type: 'declined',
          original_message_id: message.id
        }
      });
      
      console.log('✅ Quick session request declined');
      
    } catch (error) {
      console.error('❌ Error declining quick session request:', error);
      setError('Failed to decline session request');
    }
  };

  // Enhanced sendMessage
  const sendMessage = async () => {
    if (!newMessage.trim() || !currentChannel || sending) return;
    
    try {
      setSending(true);
      
      // Send message via Stream Chat
      await currentChannel.sendMessage({
        text: newMessage.trim(),
        user_id: doctorInfo._id,
        sender_type: 'doctor'
      });
      
      // Trigger FCM notification to mobile user
      try {
        console.log('🔔 Triggering mobile notification...');
        
        const response = await fetch('/api/notifications/message', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            recipientId: selectedPatient._id,
            messageContent: newMessage.trim(),
            conversationId: currentChannel.id
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('✅ FCM notification triggered successfully!', result);
        } else {
          const errorText = await response.text();
          console.error('❌ Failed to trigger FCM notification:', response.status, errorText);
        }
      } catch (notificationError) {
        console.error('❌ Error triggering notification:', notificationError);
      }
      
      setNewMessage('');
      messageInputRef.current?.focus();
      
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Utility functions
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    
    return date.toLocaleDateString();
  };

  const getPatientInitials = (patient) => {
    return `${patient.firstName?.charAt(0) || ''}${patient.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getDoctorInitials = () => {
    if (!doctorInfo) return 'DR';
    return `${doctorInfo.firstName?.charAt(0) || ''}${doctorInfo.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // 🎥 Check if session can be joined
  const canJoinSession = (sessionTime) => {
    const now = new Date();
    const sessionDate = new Date(sessionTime);
    const minutesUntilSession = Math.floor((sessionDate - now) / (1000 * 60));
    
    return minutesUntilSession <= 5 && minutesUntilSession >= -15;
  };

  if (loading) {
  return (
    <FeatureWrapper feature="Messages" showMessage={true}>
      <div className="neurolex-loading-container">
        <div className="neurolex-loading-insideContainer">
        <div className="neurolex-dashboard-spinner">
          <div className="spinner-circle outer"></div>
          <div className="spinner-circle middle"></div>
          <div className="spinner-circle inner"></div>
          <div className="spinner-center">
            <img 
              src={neurolexLogo} 
              alt="NeuroLex Logo" 
              className="loading-logo"
              onError={(e) => {
                console.warn('❌ Logo failed to load from:', neurolexLogo);
                // Fallback to messages emoji if logo doesn't load
                e.target.style.display = 'none';
                const fallback = e.target.nextElementSibling;
                if (fallback) {
                  fallback.style.display = 'flex';
                  fallback.style.alignItems = 'center';
                  fallback.style.justifyContent = 'center';
                }
              }}
              onLoad={() => {
                console.log('✅ NeuroLex logo loaded successfully:', neurolexLogo);
              }}
            />
            <div className="loading-logo-fallback" style={{ display: 'none' }}>
              💬
            </div>
          </div>
        </div>
        </div>
      </div>
    </FeatureWrapper>
  );
}

  return (
    <FeatureWrapper feature="Messages" showMessage={true}>
      <div className="doctor-messages-container">
        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{error}</span>
            <button 
              className="error-dismiss"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Sidebar Container */}
        <div className="sidebar-container">
          {/* Enhanced Header */}
          <div className="messages-header">
            <div className="header-left">
              <h1 className="messages-title">Messages</h1>
              <p className="messages-subtitle">
                Where conversations and care continue.
              </p>
            </div>
            <div className="header-icon">
              <span className="header-icon-emoji">💌</span>
            </div>
          </div>

          {/* Search Section */}
          <div className="search-section">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="patient-search"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="conversations-list">
            {filteredPatients.length === 0 ? (
              <div className="no-patients">
                <p className="no-patients-text">
                  {searchTerm ? 'No patients found matching your search.' : 'No patients with appointments found.'}
                </p>
                {!searchTerm && (
                  <p className="no-patients-subtitle">
                    Patients will appear here after they book appointments with you.
                  </p>
                )}
              </div>
            ) : (
              filteredPatients.map((patient) => (
                <div
                  key={patient._id}
                  className={`conversation-item ${selectedPatient?._id === patient._id ? 'selected' : ''}`}
                  onClick={() => selectPatient(patient)}
                >
                  <div className="conversation-avatar">
                    {patient.profilePicture ? (
                      <img src={patient.profilePicture} alt={`${patient.firstName} ${patient.lastName}`} />
                    ) : (
                      <div className="avatar-initials">{getPatientInitials(patient)}</div>
                    )}
                    <div className="online-indicator"></div>
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-header">
                      <div className="patient-name">
                        {patient.firstName} {patient.lastName}
                      </div>
                      <div className="last-message-time">
                        {patient.lastAppointment ? formatMessageTime(patient.lastAppointment) : 'New'}
                      </div>
                    </div>
                    <div className="conversation-preview">
                      <span className="appointment-count">
                        {patient.appointmentCount} appointment{patient.appointmentCount !== 1 ? 's' : ''}
                      </span>
                      {/* 🎥 Next session indicator */}
                      {patient.nextAppointment && (
                        <span className="next-session-indicator">
                          📹 Next session: {new Date(patient.nextAppointment.appointmentDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="conversation-badge">
                    <div className="unread-count">●</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="chat-area">
          {selectedPatient ? (
            <>
              {/* Enhanced Chat Header with video session button */}
              <div className="chat-header">
                <div className="chat-patient-info">
                  <div className="chat-patient-avatar">
                    {selectedPatient.profilePicture ? (
                      <img src={selectedPatient.profilePicture} alt={`${selectedPatient.firstName} ${selectedPatient.lastName}`} />
                    ) : (
                      <div className="avatar-initials">{getPatientInitials(selectedPatient)}</div>
                    )}
                  </div>
                  <div className="chat-patient-details">
                    <h3 className="chat-patient-name">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </h3>
                    <p className="chat-patient-status">
                      {selectedPatient.appointmentCount} appointment{selectedPatient.appointmentCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                
                {/* 🎥 Video session controls */}
                <div className="chat-header-actions">
                  {selectedPatient.nextAppointment && (
                    <button
                      className="create-session-btn"
                      onClick={() => setShowCreateSessionModal(true)}
                      title="Create video session"
                    >
                      📹 Create Session
                    </button>
                  )}
                </div>
              </div>

              {/* Enhanced Messages with video session support */}
              <div className="messages-container">
                {messages.length === 0 ? (
                  <div className="no-messages">
                    <div className="no-messages-icon">💬</div>
                    <h3 className="no-messages-title">Start the conversation</h3>
                    <p className="no-messages-text">
                      Send a message to {selectedPatient.firstName} to begin your secure conversation.
                    </p>
                  </div>
                ) : (
                  <div className="messages-list">
                    {messages.map((message, index) => {
                      const isDoctor = message.user?.id === doctorInfo._id;
                      const isConsecutive = index > 0 && 
                        messages[index - 1].user?.id === message.user?.id &&
                        (new Date(message.created_at) - new Date(messages[index - 1].created_at)) < 60000;
                      
                      // 🔍 Enhanced DEBUG LOGGING for message detection
                      console.log('🎥 Message Debug:', {
                        text: message.text,
                        type: message.type,
                        extraData: message.extraData,
                        attachments: message.attachments,
                        isDoctor: isDoctor,
                        userId: message.user?.id,
                        doctorId: doctorInfo._id,
                        detectsAsQuickSession: detectQuickSessionRequest(message)
                      });
                      
                      // 🎥 PRIORITY 1: Check for video session invitation with extraData
                      if (message.extraData?.message_type === 'video_session_invitation') {
                        console.log('🎥 Video session invitation detected:', message.extraData);
                        
                        const sessionData = message.extraData;
                        
                        if (sessionData.session_id && sessionData.session_time) {
                          return (
                            <VideoSessionInvitation
                              key={message.id}
                              sessionId={sessionData.session_id}
                              sessionTime={sessionData.session_time}
                              sessionDuration={parseInt(sessionData.session_duration) || 60}
                              status={sessionData.status || 'scheduled'}
                              canJoin={canJoinSession(sessionData.session_time)}
                              onJoinSession={joinVideoSession}
                              userRole="doctor"
                            />
                          );
                        } else {
                          console.error('❌ Missing required session data:', sessionData);
                          return (
                            <div key={message.id} className="error-message">
                              <span>⚠️ Invalid video session invitation</span>
                            </div>
                          );
                        }
                      }
                      
                      // 🎥 PRIORITY 2: Check for video session status messages
                      if (message.extraData?.message_type === 'video_session_status') {
                        return (
                          <div key={message.id} className="session-status-message">
                            <div className="session-status-content">
                              <span className="session-status-icon">🎥</span>
                              <span className="session-status-text">{message.text}</span>
                              <span className="session-status-time">
                                {formatMessageTime(message.created_at)}
                              </span>
                            </div>
                          </div>
                        );
                      }
                      
                      // 🎥 PRIORITY 3: Check for quick session requests (TEXT-BASED)
                      if (detectQuickSessionRequest(message)) {
                        console.log('🎥 Quick session request detected - rendering special UI');
                        return (
                          <QuickSessionRequestMessage
                            key={message.id}
                            message={message}
                            onAccept={handleQuickSessionRequest}
                            onDecline={handleDeclineQuickSession}
                          />
                        );
                      }
                      
                      // 🎥 PRIORITY 4: Regular message rendering
                      return (
                        <div
                          key={message.id}
                          className={`message ${isDoctor ? 'doctor-message' : 'patient-message'} ${isConsecutive ? 'consecutive' : ''}`}
                        >
                          {!isConsecutive && (
                            <div className="message-avatar">
                              {isDoctor ? (
                                currentUser?.profilePicture || doctorInfo?.profilePicture ? (
                                  <img 
                                    src={currentUser?.profilePicture || doctorInfo?.profilePicture} 
                                    alt="Doctor" 
                                    className="message-avatar-image"
                                    style={{ width: '28.65px', height: '28.65px', borderRadius: '50%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div className="avatar-initials doctor-avatar">
                                    {getDoctorInitials()}
                                  </div>
                                )
                              ) : (
                                selectedPatient.profilePicture ? (
                                  <img src={selectedPatient.profilePicture} alt={selectedPatient.firstName} />
                                ) : (
                                  <div className="avatar-initials">{getPatientInitials(selectedPatient)}</div>
                                )
                              )}
                            </div>
                          )}
                          <div className="message-content">
                            {!isConsecutive && (
                              <div className="message-header">
                                <span className="message-time">
                                  {formatMessageTime(message.created_at)}
                                </span>
                              </div>
                            )}
                            <div className="message-text">
                              {message.text}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="message-input-container">
                <div className="message-input-wrapper">
                  <textarea
                    ref={messageInputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={`Message ${selectedPatient.firstName}...`}
                    className="message-input"
                    rows="1"
                    disabled={sending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="send-button"
                    style={{ backgroundColor: '#A7D7C5' }}
                  >
                    {sending ? '' : 'Send'}
                  </button>
                </div>
                <div className="message-input-hint">
                  Press Enter to send, Shift+Enter for new line
                </div>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="no-chat-icon">💬</div>
              <h3 className="no-chat-title">Select a patient to start messaging</h3>
              <p className="no-chat-text">
                Choose a patient from the list to begin a secure conversation through {platformName}.
              </p>
            </div>
          )}
        </div>

        {/* 🎥 VIDEO SESSION MODALS AND OVERLAYS */}
        {showCreateSessionModal && selectedPatient && (
          <CreateSessionModal
            patient={selectedPatient}
            onClose={() => setShowCreateSessionModal(false)}
            onCreateSession={createVideoSession}
          />
        )}

        {activeVideoSession && (
          <VideoSessionOverlay
            sessionId={activeVideoSession.sessionId}
            userRole={activeVideoSession.userRole}
            patientInfo={activeVideoSession.patientInfo}
            doctorInfo={activeVideoSession.doctorInfo}
            onEndSession={endVideoSession}
            onUpdateStatus={updateSessionStatus}
          />
        )}
      </div>
    </FeatureWrapper>
  );
};

export default DoctorMessages;